import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import Tabs from "@/components/base/Tabs";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager, type Page } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { getClasses } from "@/services/academicService";
import { getCycles, getActiveSchoolYear } from "@/services/schoolService";
import {
  rejoinSchool,
  getEnrollments,
  getGuardians,
  getStudentGuardians,
  getStudents,
} from "@/services/studentService";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate, plural } from "@/utils/format";
import type { Enrollment, Student } from "@/types";
import LeaveSchoolModal from "./components/LeaveSchoolModal";
import StudentFormModal from "./components/StudentFormModal";
import StudentProfileModal from "./components/StudentProfileModal";
import StudentCardsModal from "./components/StudentCardsModal";
import { cycleIdOf } from "@/utils/cycleScope";
import { ENROLLMENT_STATUS_LABEL, ENROLLMENT_STATUS_TONE } from "@/utils/enrollment";
import { useUrlSearch } from "@/hooks/useUrlSearch";

async function load() {
  const [students, guardians, links, enrollments, classes, cycles] = await Promise.all([
    getStudents(),
    getGuardians(),
    getStudentGuardians(),
    getEnrollments(),
    getClasses(),
    getCycles(),
  ]);
  return { students, guardians, links, enrollments, classes, cycles };
}

export default function StudentsPage() {
  const { notify } = useToast();
  const { allowedCycleIds, cycleScope, can, role } = useAuth();
  const schoolYear = getActiveSchoolYear();
  const { data, loading, error, reload } = useAsync(load, []);

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useUrlSearch();
  const [classFilter, setClassFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const pagerPrimaire = usePager();
  const pagerSecondaire = usePager();
  const pagerOther = usePager();
  const [profile, setProfile] = useState<Student | null>(null);
  const [leaving, setLeaving] = useState<{ student: Student; enrollment: Enrollment } | null>(null);
  const [toRejoin, setToRejoin] = useState<{ student: Student; enrollment: Enrollment } | null>(null);
  /** Les élèves qui ont quitté l'école ne figurent plus dans les listes, sauf à la demande (ou par la recherche). */
  const [showLeft, setShowLeft] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);

  // « ?open=<id> » (résultat de la recherche de l'en-tête) : ouvre directement la fiche de l'élève.
  const [params, setParams] = useSearchParams();
  const openId = params.get("open");
  useEffect(() => {
    if (!openId || !data) return;
    const found = data.students.find((s) => s.id === openId);
    if (found) setProfile(found);
    setParams(
      (current) => {
        current.delete("open");
        return current;
      },
      { replace: true },
    );
  }, [openId, data, setParams]);

  if (loading && !data) return <LoadingState label="Chargement des élèves..." />;
  if (error || !data) return <div className="text-sm text-foreground-600">{error}</div>;

  const { students, guardians, links, enrollments, classes, cycles } = data;
  const primaireId = cycleIdOf(cycles, "PRIMAIRE");
  const secondaireId = cycleIdOf(cycles, "SECONDAIRE");

  const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
  const scopedEnrollments = enrollments.filter((e) => allowedCycleIds.includes(e.cycleId));
  const allowedStudentIds = new Set(scopedEnrollments.map((e) => e.studentId));
  const scopedStudents = cycleScope === "GLOBAL" ? students : students.filter((s) => allowedStudentIds.has(s.id));

  const currentEnrollment = (studentId: string) =>
    scopedEnrollments.find((e) => e.studentId === studentId && e.schoolYear === schoolYear && e.status !== "CANCELLED");

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? "Non inscrit";

  const primaryFor = (studentId: string) => {
    const link = links.find((l) => l.studentId === studentId && l.isPrimary) ?? links.find((l) => l.studentId === studentId);
    if (!link) return null;
    return guardians.find((g) => g.id === link.guardianId) ?? null;
  };

  const filtered = scopedStudents.filter((s) => {
    const enr = currentEnrollment(s.id);
    const matchesTab = tab === "all" || (enr && enr.cycleId === tab);
    const matchesClass = classFilter === "all" || (enr && enr.classId === classFilter);
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      s.lastName.toLowerCase().includes(q) ||
      s.firstName.toLowerCase().includes(q) ||
      s.matricule.toLowerCase().includes(q);
    // Élève parti : hors des listes, sauf si on demande à le voir ou si on le cherche.
    const visible = enr?.status !== "LEFT" || showLeft || !!q;
    return matchesTab && matchesClass && matchesSearch && visible;
  });

  const handleRejoin = async (target: { student: Student; enrollment: Enrollment }) => {
    try {
      await rejoinSchool(target.enrollment.id);
      await reload();
      notify(`${target.student.firstName} ${target.student.lastName} est réintégré(e).`);
    } catch (err) {
      notify(err instanceof Error ? err.message : "Réintégration impossible.", "error");
    }
  };

  const renderTable = (page: Page<Student>) => (
    <>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-sm">
        <thead>
          <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
            <th className="px-4 py-3 font-medium">Élève</th>
            <th className="px-4 py-3 font-medium">Matricule</th>
            <th className="px-4 py-3 font-medium">Classe</th>
            <th className="px-4 py-3 font-medium">Responsable</th>
            <th className="px-4 py-3 font-medium">Statut</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {page.items.map((s) => {
            const enr = currentEnrollment(s.id);
            const guardian = primaryFor(s.id);
            return (
              <tr key={s.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar lastName={s.lastName} firstName={s.firstName} src={s.photo || undefined} size="sm" />
                    <div>
                      <p className="font-medium text-foreground-900">
                        {s.firstName} {s.lastName}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {s.gender === "M" ? "Masculin" : "Féminin"} • {formatDate(s.birthDate)}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-foreground-600">{s.matricule}</td>
                <td className="px-4 py-3">
                  <span className="text-foreground-800">{className(enr?.classId ?? "")}</span>
                </td>
                <td className="px-4 py-3">
                  {guardian ? (
                    <div>
                      <p className="text-foreground-800">
                        {guardian.firstName} {guardian.lastName}
                      </p>
                      <p className="text-xs text-foreground-500">{guardian.phone}</p>
                    </div>
                  ) : (
                    <span className="text-foreground-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {enr ? (
                    <span title={enr.status === "LEFT" ? `Parti le ${formatDate(enr.leftAt ?? "")} — ${enr.leftReason ?? ""}` : undefined}>
                      <Badge tone={ENROLLMENT_STATUS_TONE[enr.status]}>{ENROLLMENT_STATUS_LABEL[enr.status]}</Badge>
                    </span>
                  ) : (
                    <Badge tone="neutral" icon="ri-time-line">
                      Non inscrit
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setProfile(s)}
                      className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
                      aria-label="Voir le dossier"
                      title="Voir le dossier"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    <button
                      onClick={() => {
                        setEditing(s);
                        setFormOpen(true);
                      }}
                      className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
                      aria-label="Modifier"
                      title="Modifier"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    {enr?.status === "ACTIVE" && can("enrollments:update") && (
                      <button
                        onClick={() => setLeaving({ student: s, enrollment: enr })}
                        className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                        aria-label="Déclarer un départ"
                        title="Déclarer un départ (l'élève quitte l'école)"
                      >
                        <i className="ri-logout-box-r-line"></i>
                      </button>
                    )}
                    {enr?.status === "LEFT" && role?.isSystem && (
                      <button
                        onClick={() => setToRejoin({ student: s, enrollment: enr })}
                        className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                        aria-label="Réintégrer"
                        title="Réintégrer (départ déclaré par erreur)"
                      >
                        <i className="ri-login-box-line"></i>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {page.total === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-sm text-foreground-400">
                Aucun élève ne correspond à votre recherche.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <Pagination pager={page} className="border-t border-background-200" />
    </>
  );

  const primaireRows = filtered.filter((s) => currentEnrollment(s.id)?.cycleId === primaireId);
  const secondaireRows = filtered.filter((s) => currentEnrollment(s.id)?.cycleId === secondaireId);
  const otherRows = filtered.filter((s) => !currentEnrollment(s.id));
  const filterKey = `${tab}|${classFilter}|${search}`;

  const showPrimaire = allowedCycleIds.includes(primaireId) && (tab === "all" || tab === primaireId);
  const showSecondaire = allowedCycleIds.includes(secondaireId) && (tab === "all" || tab === secondaireId);

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Gestion des élèves"
        subtitle={`${scopedStudents.length} dossiers permanents — année ${schoolYear}`}
        icon="ri-user-3-line"
        actions={
          <>
            {can("documents:cards") && (
              <Button variant="outline" icon="ri-bank-card-line" onClick={() => setCardsOpen(true)}>
                Cartes scolaires
              </Button>
            )}
            <Button
              icon="ri-user-add-line"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Nouvel élève
            </Button>
          </>
        }
      />

      <CycleScopeBanner className="mb-5" />

      <Card padded={false} className="mb-5">
        <div className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
          <Tabs
            tabs={[
              { id: "all", label: "Tous", count: scopedStudents.length },
              ...visibleCycles.map((c) => ({
                id: c.id,
                label: c.label,
                count: scopedStudents.filter((s) => currentEnrollment(s.id)?.cycleId === c.id).length,
              })),
            ]}
            active={tab}
            onChange={setTab}
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, prénom ou matricule..."
                className="w-full sm:w-56 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 cursor-pointer focus:border-primary-400 focus:outline-none"
            >
              <option value="all">Toutes les classes</option>
              {visibleCycles.map((c) => (
                <optgroup key={c.id} label={c.label}>
                  {classes
                    .filter((cls) => cls.cycleId === c.id)
                    .map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                </optgroup>
                ))}
            </select>
            <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700">
              <input type="checkbox" checked={showLeft} onChange={(e) => setShowLeft(e.target.checked)} className="h-4 w-4 accent-primary-600" />
              Élèves partis
            </label>
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        {showPrimaire && (
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-warning-50 text-warning-700 flex items-center justify-center">
                  <i className="ri-seedling-line"></i>
                </span>
                <h3 className="text-sm font-semibold font-heading text-foreground-950">Cycle Primaire</h3>
              </div>
              <Badge tone="warning">{plural(primaireRows.length, "élève")}</Badge>
            </div>
            {renderTable(pagerPrimaire.slice(primaireRows, filterKey))}
          </Card>
        )}

        {showSecondaire && (
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className="ri-book-read-line"></i>
                </span>
                <h3 className="text-sm font-semibold font-heading text-foreground-950">Cycle Secondaire</h3>
              </div>
              <Badge tone="secondary">{plural(secondaireRows.length, "élève")}</Badge>
            </div>
            {renderTable(pagerSecondaire.slice(secondaireRows, filterKey))}
          </Card>
        )}

        {cycleScope === "GLOBAL" && tab === "all" && otherRows.length > 0 && (
          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-background-200 text-foreground-600 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                <h3 className="text-sm font-semibold font-heading text-foreground-950">
                  Dossiers sans inscription cette année
                </h3>
              </div>
              <Badge tone="neutral">{otherRows.length}</Badge>
            </div>
            {renderTable(pagerOther.slice(otherRows, filterKey))}
          </Card>
        )}
      </div>

      <StudentFormModal
        open={formOpen}
        editing={editing}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
      />
      <StudentCardsModal
        open={cardsOpen}
        onClose={() => setCardsOpen(false)}
        students={scopedStudents}
        enrollments={scopedEnrollments}
        classes={classes}
        schoolYear={schoolYear}
      />
      <StudentProfileModal
        open={profile !== null}
        student={profile}
        classes={classes}
        onClose={() => setProfile(null)}
      />
      <LeaveSchoolModal target={leaving} onClose={() => setLeaving(null)} onDone={reload} />
      <ConfirmDialog
        open={toRejoin !== null}
        title="Réintégrer l'élève"
        message={`Annuler le départ de ${toRejoin?.student.firstName} ${toRejoin?.student.lastName} ? Il revient dans sa classe et redoit toutes les tranches de l'année.`}
        confirmLabel="Réintégrer"
        onClose={() => setToRejoin(null)}
        onConfirm={() => {
          if (toRejoin) void handleRejoin(toRejoin);
        }}
      />
    </div>
  );
}